import * as d3 from 'd3'
import { TRANSITION_DURATION } from './constants'
import { SCALE_EXTENT, fitScale } from './fit'
import state from './state'

export default class Viewport {
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>
  container: d3.Selection<SVGGElement, unknown, null, undefined>
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>
  width: number
  height: number
  /**
   * Whether the reader has zoomed or panned by hand since the last fit.
   *
   * ⚠ `event.sourceEvent` is the discriminator and it is exact: d3 leaves it null for a
   * programmatic `zoom.transform` and sets it to the wheel/pointer event for a real one. A flag
   * set from anywhere else would be turned on by our own fit and never go off again.
   */
  private userZoomed = false

  constructor(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>, width: number, height: number) {
    this.width = width
    this.height = height
    this.svg = svg

    this.container = svg.append('g').attr('class', 'zoom')
    this.zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent(SCALE_EXTENT)
      .on('zoom', event => {
        if (event.sourceEvent) this.userZoomed = true
        this.container.attr('transform', event.transform)
      })

    const savedScale = state.getZoomScale() ?? 1
    this.svg.call(this.zoom.transform, d3.zoomIdentity.translate(width / 2, height / 2).scale(savedScale))
  }

  getCurrentScale(): number {
    const transform = d3.zoomTransform(this.container.node() as SVGGElement)
    return transform.k
  }

  /**
   * Follow the window. A rotation, a split screen, a dragged-in devtools pane and a phone are all
   * this same event, and the graph has to stay usable through every one of them.
   *
   * ⚠⚠ RE-FITS ONLY IF THE READER HAS NOT ZOOMED BY HAND. Re-fitting unconditionally would throw
   * away the frame they chose every time the window moved a pixel — and `useWindowSize` is not
   * debounced, so that is once per resize event, not once per resize. When they HAVE zoomed, the
   * old behaviour is the right one: keep their scale and their centre.
   *
   * ⚠ No transition on this path. It is driven by a resize stream, and a 300 ms tween started on
   * every event of that stream never finishes one before the next replaces it, which reads as the
   * graph lagging behind the window edge.
   */
  updateSize(newWidth: number, newHeight: number, nodes?: GraphNode[]) {
    if (!this.userZoomed && nodes && nodes.length) {
      this.width = newWidth
      this.height = newHeight
      this.centerGraph(nodes, { animate: false })
      return
    }

    const currentTransform = d3.zoomTransform(this.container.node() as SVGGElement)
    const deltaTranslateX = ((newWidth - this.width) / 2) * currentTransform.k
    const deltaTranslateY = ((newHeight - this.height) / 2) * currentTransform.k
    const newTranslateX = currentTransform.x + deltaTranslateX
    const newTranslateY = currentTransform.y + deltaTranslateY

    const updatedTransform = d3.zoomIdentity.translate(newTranslateX, newTranslateY).scale(currentTransform.k)

    this.width = newWidth
    this.height = newHeight
    this.svg.call(this.zoom.transform, updatedTransform)
  }

  /**
   * Put the whole graph in the middle of the window, AT NATURAL SIZE OR SMALLER.
   *
   * ⚠⚠ THIS USED TO END ON `.scale(1)`, which ran a large graph off the edge of a small window:
   * `layout.test.ts` had to carry a `height < 760` assertion to stop the LAYOUT outgrowing a
   * laptop, because the VIEWPORT could not adapt — a constraint on the wrong half of the problem.
   * Shrinking here is what lets that gate be a sanity bound instead of a design limit.
   *
   * ⚠⚠ AND IT STOPS AT 1, WHICH IS THE OTHER HALF AND WAS BRIEFLY WRONG. Enlarging to fill the
   * window is a different question from fitting in it, and on a small fleet the answer is pure
   * magnification — bigger glyphs, bigger gaps, no more information. See `FIT_MAX_SCALE`.
   *
   * ⚠ The fit reads the extent of the drawn nodes, so it follows what is drawn: more nodes zoom
   * out to take them in rather than pushing them off-screen, and fewer give that size back — up to
   * natural size and no further.
   */
  centerGraph(nodes: GraphNode[], { animate = true }: { animate?: boolean } = {}) {
    const minX = d3.min(nodes, d => d.x) ?? 0
    const maxX = d3.max(nodes, d => d.x) ?? 0
    const minY = d3.min(nodes, d => d.y) ?? 0
    const maxY = d3.max(nodes, d => d.y) ?? 0
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2
    const scale = fitScale(maxX - minX, maxY - minY, this.width, this.height)

    // d3 maps a point p to p * k + t, so the translation that lands the content's centre on the
    // window's centre has to be taken AFTER the scale. Multiplying it before is the classic
    // version of this bug and leaves the picture off-centre by (k - 1) * centre.
    const transform = d3.zoomIdentity
      .translate(this.width / 2 - centerX * scale, this.height / 2 - centerY * scale)
      .scale(scale)

    // The graph is framed for them again, so their own framing is no longer what is on screen.
    this.userZoomed = false
    // Record the scale the fit chose rather than reading it back after the transition, which
    // would still be the old one — the tween has not started when the caller saves.
    state.setZoomScale(scale)
    // ⚠ `.interrupt()` first, for the idle-tab hang `core.ts` describes: a draw that changes the
    // drawn set re-frames through here, and in a covered window each one queued one more tween.
    if (animate) this.svg.interrupt().transition().duration(TRANSITION_DURATION).call(this.zoom.transform, transform)
    else this.svg.call(this.zoom.transform, transform)
  }

}
